import React, { useState, useEffect } from "react";
import TipTapEditor from "./components/TipTapEditor";
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  deleteDoc, 
  updateDoc, 
  orderBy, 
  query,
  setDoc,
  getDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { db, storage, auth } from "./firebase";
import "./styles.css";

export default function HomepageManager() {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCompetition, setEditingCompetition] = useState(null);
  const [quickResultsEdit, setQuickResultsEdit] = useState({});
  const [activeTab, setActiveTab] = useState("competitions");
  const [rulesSubTab, setRulesSubTab] = useState("solo"); // New state for Rules sub-tabs
  const [rulesContentSolo, setRulesContentSolo] = useState("");
  const [rulesContentGroup, setRulesContentGroup] = useState("");
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [cancelModal, setCancelModal] = useState({ show: false, competition: null, reason: "" });
  
  // Announcement banner state
  const [announcementSettings, setAnnouncementSettings] = useState({
    enabled: true,
    text: "",
    icon: "⚠️",
    textSize: "medium" // small, medium, large
  });
  const [announcementLoading, setAnnouncementLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    date: "",
    location: "",
    website: "",
    registration: "",
    schedule: "",
    scheduleUpdated: false,
    results: "",
    type: "upcoming", // upcoming or past
    logo: null,
    logoUrl: "",
    categories: [],
    sortOrder: 0,
    cancelled: false,
    cancellationReason: "",
    playlist: [], // Array of {name, displayName, url, category, round, dance} for audio tracks
    prizeMoney: null // { title: string, categories: { categoryName: { prizes: [{place: string, amount: number}] } } }
  });
  const [playlistFiles, setPlaylistFiles] = useState([]); // Files to upload with metadata
  const [uploadingPlaylist, setUploadingPlaylist] = useState(false);
  const [newTrackCategory, setNewTrackCategory] = useState("");
  const [newTrackRound, setNewTrackRound] = useState("");
  const [newTrackDance, setNewTrackDance] = useState("");
  const [draggedTrackIndex, setDraggedTrackIndex] = useState(null);

  // Prize Money state
  const [activePrizeCategory, setActivePrizeCategory] = useState(null);
  const [newPrizeCategory, setNewPrizeCategory] = useState("");

  // Prize Money helper functions
  const initializePrizeMoney = () => {
    if (!formData.prizeMoney) {
      setFormData(prev => ({
        ...prev,
        prizeMoney: {
          title: "",
          categories: {}
        }
      }));
    }
  };

  const updatePrizeMoneyTitle = (title) => {
    setFormData(prev => ({
      ...prev,
      prizeMoney: {
        ...prev.prizeMoney,
        title: title
      }
    }));
  };

  const addPrizeCategory = () => {
    if (!newPrizeCategory.trim()) return;
    initializePrizeMoney();
    setFormData(prev => ({
      ...prev,
      prizeMoney: {
        ...prev.prizeMoney,
        categories: {
          ...prev.prizeMoney?.categories,
          [newPrizeCategory.trim()]: {
            prizes: [{ place: "1st", amount: "" }]
          }
        }
      }
    }));
    setActivePrizeCategory(newPrizeCategory.trim());
    setNewPrizeCategory("");
  };

  const removePrizeCategory = (categoryName) => {
    setFormData(prev => {
      const newCategories = { ...prev.prizeMoney?.categories };
      delete newCategories[categoryName];
      return {
        ...prev,
        prizeMoney: {
          ...prev.prizeMoney,
          categories: newCategories
        }
      };
    });
    if (activePrizeCategory === categoryName) {
      const remainingCategories = Object.keys(formData.prizeMoney?.categories || {}).filter(c => c !== categoryName);
      setActivePrizeCategory(remainingCategories[0] || null);
    }
  };

  const addPrizeToCategory = (categoryName) => {
    setFormData(prev => ({
      ...prev,
      prizeMoney: {
        ...prev.prizeMoney,
        categories: {
          ...prev.prizeMoney?.categories,
          [categoryName]: {
            ...prev.prizeMoney?.categories[categoryName],
            prizes: [
              ...prev.prizeMoney?.categories[categoryName]?.prizes || [],
              { place: "", amount: "" }
            ]
          }
        }
      }
    }));
  };

  const updatePrize = (categoryName, prizeIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      prizeMoney: {
        ...prev.prizeMoney,
        categories: {
          ...prev.prizeMoney?.categories,
          [categoryName]: {
            ...prev.prizeMoney?.categories[categoryName],
            prizes: prev.prizeMoney?.categories[categoryName]?.prizes.map((prize, i) =>
              i === prizeIndex ? { ...prize, [field]: value } : prize
            )
          }
        }
      }
    }));
  };

  const removePrize = (categoryName, prizeIndex) => {
    setFormData(prev => ({
      ...prev,
      prizeMoney: {
        ...prev.prizeMoney,
        categories: {
          ...prev.prizeMoney?.categories,
          [categoryName]: {
            ...prev.prizeMoney?.categories[categoryName],
            prizes: prev.prizeMoney?.categories[categoryName]?.prizes.filter((_, i) => i !== prizeIndex)
          }
        }
      }
    }));
  };

  const clearAllPrizeMoney = () => {
    if (window.confirm("Are you sure you want to remove all prize money data?")) {
      setFormData(prev => ({ ...prev, prizeMoney: null }));
      setActivePrizeCategory(null);
    }
  };

  // Playlist category options
  const playlistCategories = [
    "Adult Solo Latin",
    "Adult Solo Contemporary", 
    "Adult Solo Hip Hop",
    "Adult Group Latin",
    "Adult Group Contemporary",
    "Adult Group Hip Hop",
    "Youth Solo Latin",
    "Youth Solo Contemporary",
    "Youth Solo Hip Hop",
    "Youth Group Latin",
    "Youth Group Contemporary",
    "Youth Group Hip Hop",
    "Junior II Solo Latin",
    "Junior II Solo Contemporary",
    "Junior II Solo Hip Hop",
    "Junior II Group Latin",
    "Junior II Group Contemporary",
    "Junior II Group Hip Hop",
    "Other"
  ];

  const playlistRounds = ["Final", "Semi Final", "Quarter Final", "Preliminary", "Other"];
  
  const danceNames = [
    "Cha Cha Cha", "Samba", "Rumba", "Paso Doble", "Jive",
    "Waltz", "Tango", "Viennese Waltz", "Foxtrot", "Quickstep",
    "Contemporary", "Hip Hop", "Commercial", "Other"
  ];

  // Available categories - 4 disciplines with Solo & Group
  const disciplines = ["Latin", "Contemporary", "Hip Hop", "Commercial"];
  const formats = ["Solo", "Group"];

  // Generate all possible category combinations
  const availableCategories = [];
  disciplines.forEach(discipline => {
    formats.forEach(format => {
      availableCategories.push(`${discipline} - ${format}`);
    });
  });

  useEffect(() => {
    // Monitor authentication state
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
      
      if (currentUser) {
        fetchCompetitions();
        fetchRulesContent();
        fetchAnnouncementSettings();
      }
    });

    return () => unsubscribe();
  }, []);

  // Fetch announcement settings
  const fetchAnnouncementSettings = async () => {
    try {
      const announcementRef = doc(db, "homepage_settings", "announcement");
      const announcementDoc = await getDoc(announcementRef);
      
      if (announcementDoc.exists()) {
        setAnnouncementSettings(announcementDoc.data());
      }
    } catch (error) {
      console.error("Error fetching announcement settings:", error);
    }
  };

  // Save announcement settings
  const saveAnnouncementSettings = async () => {
    try {
      setAnnouncementLoading(true);
      const announcementRef = doc(db, "homepage_settings", "announcement");
      await setDoc(announcementRef, {
        ...announcementSettings,
        updatedAt: new Date().toISOString()
      });
      alert("✅ Announcement settings saved successfully!");
    } catch (error) {
      console.error("Error saving announcement settings:", error);
      alert("❌ Error saving announcement settings: " + error.message);
    } finally {
      setAnnouncementLoading(false);
    }
  };

  const fetchRulesContent = async () => {
    try {
      // Fetch Solo rules
      const rulesSoloQuery = query(collection(db, "homepage_rules_solo"));
      const snapshotSolo = await getDocs(rulesSoloQuery);
      if (!snapshotSolo.empty) {
        const rulesDoc = snapshotSolo.docs[0];
        setRulesContentSolo(rulesDoc.data().content || "");
      }

      // Fetch Group rules
      const rulesGroupQuery = query(collection(db, "homepage_rules_group"));
      const snapshotGroup = await getDocs(rulesGroupQuery);
      if (!snapshotGroup.empty) {
        const rulesDoc = snapshotGroup.docs[0];
        setRulesContentGroup(rulesDoc.data().content || "");
      }
    } catch (error) {
      console.error("Error fetching rules:", error);
    }
  };

  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      const competitionsQuery = query(
        collection(db, "homepage_competitions"),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(competitionsQuery);
      const competitionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort competitions by sortOrder, then by createdAt
      const sortCompetitions = (competitions) => {
        return competitions.sort((a, b) => {
          const aOrder = a.sortOrder || 0;
          const bOrder = b.sortOrder || 0;
          if (aOrder !== bOrder) {
            return aOrder - bOrder; // Lower numbers first
          }
          // If same order, sort by createdAt (newest first)
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        });
      };
      
      setCompetitions(sortCompetitions(competitionsData));
    } catch (error) {
      console.error("Error fetching competitions:", error);
      alert("Error fetching competitions");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "categories") {
      const updatedCategories = checked
        ? [...formData.categories, value]
        : formData.categories.filter(cat => cat !== value);
      
      setFormData(prev => ({
        ...prev,
        categories: updatedCategories
      }));
    } else if (type === "file") {
      setFormData(prev => ({
        ...prev,
        [name]: e.target.files[0]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const uploadLogo = async (logoFile) => {
    if (!logoFile) return null;
    
    if (!user) {
      throw new Error("You must be logged in to upload files");
    }
    
    try {
      const timestamp = Date.now();
      // Clean filename to avoid issues
      const cleanFileName = logoFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `homepage_logos/${timestamp}_${cleanFileName}`;
      const logoRef = ref(storage, fileName);
      
      console.log("Uploading logo to:", fileName);
      await uploadBytes(logoRef, logoFile);
      const downloadURL = await getDownloadURL(logoRef);
      console.log("Upload successful, URL:", downloadURL);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading logo:", error);
      if (error.code === 'storage/unauthorized') {
        throw new Error("Permission denied. Please make sure you're logged in and try again.");
      } else if (error.code === 'storage/quota-exceeded') {
        throw new Error("Storage quota exceeded. Please contact support.");
      } else {
        throw new Error(`Upload failed: ${error.message}`);
      }
    }
  };

  const uploadPlaylistTrack = async (audioFile, competitionName) => {
    if (!audioFile) return null;
    
    if (!user) {
      throw new Error("You must be logged in to upload files");
    }
    
    try {
      const timestamp = Date.now();
      // Clean competition name and filename
      const cleanCompName = competitionName.replace(/[^a-zA-Z0-9]/g, '_');
      const cleanFileName = audioFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `competition_playlists/${cleanCompName}/${timestamp}_${cleanFileName}`;
      const trackRef = ref(storage, fileName);
      
      console.log("Uploading track to:", fileName);
      await uploadBytes(trackRef, audioFile);
      const downloadURL = await getDownloadURL(trackRef);
      console.log("Track upload successful, URL:", downloadURL);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading track:", error);
      if (error.code === 'storage/unauthorized') {
        throw new Error("Permission denied. Please make sure you're logged in and try again.");
      } else if (error.code === 'storage/quota-exceeded') {
        throw new Error("Storage quota exceeded. Please contact support.");
      } else {
        throw new Error(`Upload failed: ${error.message}`);
      }
    }
  };

  const handlePlaylistFileChange = (e) => {
    const files = Array.from(e.target.files);
    // Add files with default metadata
    const filesWithMetadata = files.map(file => ({
      file: file,
      displayName: file.name.replace(/\.[^/.]+$/, ""), // Remove extension for display
      category: newTrackCategory || "",
      round: newTrackRound || "",
      dance: newTrackDance || ""
    }));
    setPlaylistFiles(prev => [...prev, ...filesWithMetadata]);
  };

  const removePlaylistFile = (index) => {
    setPlaylistFiles(prev => prev.filter((_, i) => i !== index));
  };

  const updatePlaylistFileMetadata = (index, field, value) => {
    setPlaylistFiles(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const updateExistingTrackMetadata = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      playlist: prev.playlist.map((track, i) => 
        i === index ? { ...track, [field]: value } : track
      )
    }));
  };

  // Drag and drop for reordering playlist tracks
  const handleTrackDragStart = (index) => {
    setDraggedTrackIndex(index);
  };

  const handleTrackDragOver = (e, index) => {
    e.preventDefault();
    if (draggedTrackIndex === null || draggedTrackIndex === index) return;
    
    // Reorder the playlist
    setFormData(prev => {
      const newPlaylist = [...prev.playlist];
      const draggedItem = newPlaylist[draggedTrackIndex];
      newPlaylist.splice(draggedTrackIndex, 1);
      newPlaylist.splice(index, 0, draggedItem);
      setDraggedTrackIndex(index);
      return { ...prev, playlist: newPlaylist };
    });
  };

  const handleTrackDragEnd = () => {
    setDraggedTrackIndex(null);
  };

  const moveTrackUp = (index) => {
    if (index === 0) return;
    setFormData(prev => {
      const newPlaylist = [...prev.playlist];
      [newPlaylist[index - 1], newPlaylist[index]] = [newPlaylist[index], newPlaylist[index - 1]];
      return { ...prev, playlist: newPlaylist };
    });
  };

  const moveTrackDown = (index) => {
    setFormData(prev => {
      if (index >= prev.playlist.length - 1) return prev;
      const newPlaylist = [...prev.playlist];
      [newPlaylist[index], newPlaylist[index + 1]] = [newPlaylist[index + 1], newPlaylist[index]];
      return { ...prev, playlist: newPlaylist };
    });
  };

  const removeExistingTrack = (index) => {
    setFormData(prev => ({
      ...prev,
      playlist: prev.playlist.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.date) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);
      
      let logoUrl = formData.logoUrl;
      
      // Upload new logo if provided
      if (formData.logo) {
        logoUrl = await uploadLogo(formData.logo);
      }
      
      // Upload playlist files if any
      let playlistTracks = [...(formData.playlist || [])];
      if (playlistFiles.length > 0) {
        setUploadingPlaylist(true);
        for (const fileItem of playlistFiles) {
          try {
            const trackUrl = await uploadPlaylistTrack(fileItem.file, formData.name);
            playlistTracks.push({
              name: fileItem.file.name.replace(/\.[^/.]+$/, ""), // Original filename without extension
              displayName: fileItem.displayName || fileItem.file.name.replace(/\.[^/.]+$/, ""),
              url: trackUrl,
              fileName: fileItem.file.name,
              category: fileItem.category || "",
              round: fileItem.round || "",
              dance: fileItem.dance || ""
            });
          } catch (err) {
            console.error("Error uploading track:", err);
          }
        }
        setUploadingPlaylist(false);
      }

      const competitionData = {
        name: formData.name,
        date: formData.date,
        location: formData.location,
        website: formData.website,
        registration: formData.registration,
        schedule: formData.schedule,
        scheduleUpdated: formData.scheduleUpdated || false,
        results: formData.results,
        type: formData.results ? "past" : formData.type, // Auto move to past if results added
        logoUrl: logoUrl,
        categories: formData.categories,
        sortOrder: parseInt(formData.sortOrder) || 0,
        cancelled: formData.cancelled || false,
        cancellationReason: formData.cancellationReason || "",
        playlist: playlistTracks,
        prizeMoney: formData.prizeMoney || null,
        createdAt: editingCompetition ? editingCompetition.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (editingCompetition) {
        // Update existing competition
        const competitionRef = doc(db, "homepage_competitions", editingCompetition.id);
        await updateDoc(competitionRef, competitionData);
        
        if (formData.results && editingCompetition.type === "upcoming") {
          alert("Competition updated and moved to Past Competitions (results added)!");
        } else {
          alert("Competition updated successfully!");
        }
      } else {
        // Add new competition
        await addDoc(collection(db, "homepage_competitions"), competitionData);
        
        if (formData.results) {
          alert("Competition added to Past Competitions (results provided)!");
        } else {
          alert("Competition added successfully!");
        }
      }
      
      // Reset form
      setFormData({
        name: "",
        date: "",
        location: "",
        website: "",
        registration: "",
        schedule: "",
        scheduleUpdated: false,
        results: "",
        type: "upcoming",
        logo: null,
        logoUrl: "",
        categories: [],
        sortOrder: 0,
        cancelled: false,
        cancellationReason: "",
        playlist: [],
        prizeMoney: null
      });
      setPlaylistFiles([]);
      setShowForm(false);
      setEditingCompetition(null);
      
      // Refresh competitions list
      fetchCompetitions();
      
    } catch (error) {
      console.error("Error saving competition:", error);
      alert("Error saving competition");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (competition) => {
    setEditingCompetition(competition);
    setFormData({
      name: competition.name || "",
      date: competition.date || "",
      location: competition.location || "",
      website: competition.website || "",
      registration: competition.registration || "",
      schedule: competition.schedule || "",
      scheduleUpdated: competition.scheduleUpdated || false,
      results: competition.results || "",
      type: competition.type || "upcoming",
      logo: null,
      logoUrl: competition.logoUrl || "",
      categories: competition.categories || [],
      sortOrder: competition.sortOrder || 0,
      cancelled: competition.cancelled || false,
      cancellationReason: competition.cancellationReason || "",
      playlist: competition.playlist || [],
      prizeMoney: competition.prizeMoney || null
    });
    setPlaylistFiles([]);
    setShowForm(true);
  };

  // Handle marking competition as cancelled
  const handleCancelCompetition = (competition) => {
    if (competition.cancelled) {
      // Already cancelled - offer to uncancel
      if (window.confirm(`Remove cancellation from "${competition.name}"?`)) {
        uncancelCompetition(competition);
      }
    } else {
      // Show cancel modal to get reason
      setCancelModal({ show: true, competition, reason: "" });
    }
  };

  const confirmCancelCompetition = async () => {
    if (!cancelModal.competition) return;
    
    try {
      setLoading(true);
      const competitionRef = doc(db, "homepage_competitions", cancelModal.competition.id);
      await updateDoc(competitionRef, {
        cancelled: true,
        cancellationReason: cancelModal.reason || "No reason provided",
        updatedAt: new Date().toISOString()
      });
      
      alert(`"${cancelModal.competition.name}" has been marked as cancelled.`);
      setCancelModal({ show: false, competition: null, reason: "" });
      fetchCompetitions();
    } catch (error) {
      console.error("Error cancelling competition:", error);
      alert("Error cancelling competition");
    } finally {
      setLoading(false);
    }
  };

  const uncancelCompetition = async (competition) => {
    try {
      setLoading(true);
      const competitionRef = doc(db, "homepage_competitions", competition.id);
      await updateDoc(competitionRef, {
        cancelled: false,
        cancellationReason: "",
        updatedAt: new Date().toISOString()
      });
      
      alert(`"${competition.name}" is no longer marked as cancelled.`);
      fetchCompetitions();
    } catch (error) {
      console.error("Error uncancelling competition:", error);
      alert("Error uncancelling competition");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (competition) => {
    if (!window.confirm(`Are you sure you want to delete "${competition.name}"?`)) {
      return;
    }

    try {
      setLoading(true);
      
      // Delete logo from storage if exists
      if (competition.logoUrl) {
        try {
          const logoRef = ref(storage, competition.logoUrl);
          await deleteObject(logoRef);
        } catch (error) {
          console.log("Logo already deleted or doesn't exist");
        }
      }
      
      // Delete competition document
      await deleteDoc(doc(db, "homepage_competitions", competition.id));
      alert("Competition deleted successfully!");
      
      // Refresh competitions list
      fetchCompetitions();
      
    } catch (error) {
      console.error("Error deleting competition:", error);
      alert("Error deleting competition");
    } finally {
      setLoading(false);
    }
  };

  const moveToPast = async (competition) => {
    if (!window.confirm(`Move "${competition.name}" to Past Competitions?`)) {
      return;
    }

    try {
      setLoading(true);
      const competitionRef = doc(db, "homepage_competitions", competition.id);
      await updateDoc(competitionRef, {
        type: "past",
        updatedAt: new Date().toISOString()
      });
      
      alert("Competition moved to Past Competitions!");
      fetchCompetitions();
      
    } catch (error) {
      console.error("Error moving competition to past:", error);
      alert("Error moving competition to past");
    } finally {
      setLoading(false);
    }
  };

  const moveToUpcoming = async (competition) => {
    if (!window.confirm(`Move "${competition.name}" back to Upcoming Competitions? This will also remove the results link.`)) {
      return;
    }

    try {
      setLoading(true);
      const competitionRef = doc(db, "homepage_competitions", competition.id);
      await updateDoc(competitionRef, {
        type: "upcoming",
        results: "", // Clear the results link
        updatedAt: new Date().toISOString()
      });
      
      alert("✅ Competition moved back to Upcoming Competitions and results link removed!");
      fetchCompetitions();
      
    } catch (error) {
      console.error("Error moving competition to upcoming:", error);
      alert("❌ Error moving competition to upcoming: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleCancellation = async (competition) => {
    if (competition.cancelled) {
      // Restoring - just confirm and remove cancelled status
      if (!window.confirm(`Restore "${competition.name}"? This will remove the cancelled status.`)) {
        return;
      }
      try {
        setLoading(true);
        const competitionRef = doc(db, "homepage_competitions", competition.id);
        await updateDoc(competitionRef, {
          cancelled: false,
          cancellationReason: "",
          updatedAt: new Date().toISOString()
        });
        alert("✅ Competition restored!");
        fetchCompetitions();
      } catch (error) {
        console.error("Error restoring competition:", error);
        alert("❌ Error restoring competition: " + error.message);
      } finally {
        setLoading(false);
      }
    } else {
      // Cancelling - get reason via prompt
      const reason = window.prompt(`Cancel "${competition.name}"?\n\nEnter cancellation reason (optional):`, "");
      if (reason === null) {
        // User clicked cancel button
        return;
      }

      try {
        setLoading(true);
        const competitionRef = doc(db, "homepage_competitions", competition.id);
        await updateDoc(competitionRef, {
          cancelled: true,
          cancellationReason: reason || "No reason provided",
          updatedAt: new Date().toISOString()
        });
        alert("✅ Competition marked as cancelled!");
        fetchCompetitions();
      } catch (error) {
        console.error("Error cancelling competition:", error);
        alert("❌ Error cancelling competition: " + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const moveCompetitionUp = async (competition) => {
    try {
      setLoading(true);
      const competitionRef = doc(db, "homepage_competitions", competition.id);
      const newOrder = Math.max(0, (competition.sortOrder || 0) - 1);
      await updateDoc(competitionRef, {
        sortOrder: newOrder,
        updatedAt: new Date().toISOString()
      });
      
      fetchCompetitions();
    } catch (error) {
      console.error("Error moving competition up:", error);
      alert("Error moving competition up");
    } finally {
      setLoading(false);
    }
  };

  const moveCompetitionDown = async (competition) => {
    try {
      setLoading(true);
      const competitionRef = doc(db, "homepage_competitions", competition.id);
      const newOrder = (competition.sortOrder || 0) + 1;
      await updateDoc(competitionRef, {
        sortOrder: newOrder,
        updatedAt: new Date().toISOString()
      });
      
      fetchCompetitions();
    } catch (error) {
      console.error("Error moving competition down:", error);
      alert("Error moving competition down");
    } finally {
      setLoading(false);
    }
  };

  // 🎯 Drag and Drop Functions
  const handleDragStart = (e, competition) => {
    setDraggedItem(competition);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, competition) => {
    e.preventDefault();
    setDragOverItem(competition);
  };

  const handleDrop = async (e, targetCompetition) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetCompetition.id) {
      return;
    }

    try {
      setLoading(true);
      
      // Get competitions of the same type (upcoming or past)
      const sameTypeCompetitions = competitions
        .filter(comp => comp.type === draggedItem.type)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      
      // Remove dragged item from array
      const filteredCompetitions = sameTypeCompetitions.filter(comp => comp.id !== draggedItem.id);
      
      // Find target index
      const targetIndex = filteredCompetitions.findIndex(comp => comp.id === targetCompetition.id);
      
      // Insert dragged item at target position
      const reorderedCompetitions = [...filteredCompetitions];
      reorderedCompetitions.splice(targetIndex, 0, draggedItem);
      
      // Update sortOrder for all affected competitions
      const updates = reorderedCompetitions.map((comp, index) => ({
        id: comp.id,
        sortOrder: index
      }));
      
      // Batch update all competitions
      const updatePromises = updates.map(update => 
        updateDoc(doc(db, "homepage_competitions", update.id), {
          sortOrder: update.sortOrder,
          updatedAt: new Date().toISOString()
        })
      );
      
      await Promise.all(updatePromises);
      
      // Refresh competitions list
      fetchCompetitions();
      
    } catch (error) {
      console.error("Error reordering competitions:", error);
      alert("Error reordering competitions");
    } finally {
      setLoading(false);
      setDraggedItem(null);
      setDragOverItem(null);
    }
  };

  const cancelEdit = () => {
    setShowForm(false);
    setEditingCompetition(null);
    setFormData({
      name: "",
      date: "",
      location: "",
      website: "",
      registration: "",
      schedule: "",
      scheduleUpdated: false,
      results: "",
      type: "upcoming",
      logo: null,
      logoUrl: "",
      categories: [],
      sortOrder: 0,
      cancelled: false,
      cancellationReason: "",
      playlist: [],
      prizeMoney: null
    });
  };

  const formatDate = (dateString) => {
    return dateString || "No date specified";
  };

  const handleQuickResultsChange = (competitionId, value) => {
    setQuickResultsEdit(prev => ({
      ...prev,
      [competitionId]: value
    }));
  };

  const saveQuickResults = async (competition) => {
    const resultsUrl = quickResultsEdit[competition.id];
    if (!resultsUrl || !resultsUrl.trim()) {
      alert("Please enter a results URL");
      return;
    }

    try {
      setLoading(true);
      const competitionRef = doc(db, "homepage_competitions", competition.id);
      await updateDoc(competitionRef, {
        results: resultsUrl.trim(),
        type: "past", // Auto move to past when results added
        updatedAt: new Date().toISOString()
      });
      
      alert("Results added and competition moved to Past Competitions!");
      
      // Clear the quick edit
      setQuickResultsEdit(prev => {
        const updated = { ...prev };
        delete updated[competition.id];
        return updated;
      });
      
      fetchCompetitions();
      
    } catch (error) {
      console.error("Error saving results:", error);
      alert("Error saving results");
    } finally {
      setLoading(false);
    }
  };

  const saveRulesContentSolo = async () => {
    try {
      setLoading(true);
      
      const rulesSoloQuery = query(collection(db, "homepage_rules_solo"));
      const snapshot = await getDocs(rulesSoloQuery);
      
      if (snapshot.empty) {
        await addDoc(collection(db, "homepage_rules_solo"), {
          content: rulesContentSolo,
          updatedAt: new Date().toISOString()
        });
      } else {
        const rulesDoc = snapshot.docs[0];
        await updateDoc(doc(db, "homepage_rules_solo", rulesDoc.id), {
          content: rulesContentSolo,
          updatedAt: new Date().toISOString()
        });
      }
      
      alert("Solo Rules & Regulations saved successfully!");
      
    } catch (error) {
      console.error("Error saving solo rules:", error);
      alert("Error saving solo rules");
    } finally {
      setLoading(false);
    }
  };

  const saveRulesContentGroup = async () => {
    try {
      setLoading(true);
      
      const rulesGroupQuery = query(collection(db, "homepage_rules_group"));
      const snapshot = await getDocs(rulesGroupQuery);
      
      if (snapshot.empty) {
        await addDoc(collection(db, "homepage_rules_group"), {
          content: rulesContentGroup,
          updatedAt: new Date().toISOString()
        });
      } else {
        const rulesDoc = snapshot.docs[0];
        await updateDoc(doc(db, "homepage_rules_group", rulesDoc.id), {
          content: rulesContentGroup,
          updatedAt: new Date().toISOString()
        });
      }
      
      alert("Group Rules & Regulations saved successfully!");
      
    } catch (error) {
      console.error("Error saving group rules:", error);
      alert("Error saving group rules");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <h1>🏠 Homepage Manager</h1>
      <p>Manage competitions and results displayed on the homepage</p>

      {authLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading...</p>
        </div>
      ) : !user ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2>🔒 Authentication Required</h2>
          <p>Please log in to access the Homepage Manager.</p>
          <p>You need admin privileges to manage competitions and rules.</p>
        </div>
      ) : (
        <>
          {/* Tab Navigation */}
          <div className="admin-tabs">
            <button 
              className={`admin-tab ${activeTab === "competitions" ? "active" : ""}`}
              onClick={() => setActiveTab("competitions")}
            >
              🏆 Competitions
            </button>
            <button 
              className={`admin-tab ${activeTab === "announcement" ? "active" : ""}`}
              onClick={() => setActiveTab("announcement")}
            >
              📢 Announcement
            </button>
            <button 
              className={`admin-tab ${activeTab === "rules" ? "active" : ""}`}
              onClick={() => setActiveTab("rules")}
            >
              📋 Rules & Regulations
            </button>
          </div>

      {/* Announcement Tab */}
      {activeTab === "announcement" && (
        <div style={{ 
          background: "white", 
          padding: "2rem", 
          borderRadius: "12px", 
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          marginBottom: "2rem"
        }}>
          <h2 style={{ marginTop: 0, marginBottom: "1.5rem", color: "#333" }}>
            📢 Homepage Announcement Banner
          </h2>
          <p style={{ color: "#666", marginBottom: "1.5rem" }}>
            This banner appears at the top of the homepage. Use it for important announcements.
          </p>

          {/* Enable/Disable Toggle */}
          <div style={{ 
            marginBottom: "1.5rem", 
            padding: "1rem", 
            background: announcementSettings.enabled ? "#d4edda" : "#f8d7da",
            borderRadius: "8px",
            border: `1px solid ${announcementSettings.enabled ? "#c3e6cb" : "#f5c6cb"}`
          }}>
            <label style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "0.75rem",
              cursor: "pointer",
              fontWeight: "bold",
              color: announcementSettings.enabled ? "#155724" : "#721c24"
            }}>
              <input
                type="checkbox"
                checked={announcementSettings.enabled}
                onChange={(e) => setAnnouncementSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                style={{ width: "20px", height: "20px", cursor: "pointer" }}
              />
              {announcementSettings.enabled ? "✅ Announcement is VISIBLE" : "❌ Announcement is HIDDEN"}
            </label>
          </div>

          {/* Icon Selection */}
          <div style={{ marginBottom: "1.5rem" }}>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold", color: "#333" }}>
              Icon
            </label>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              {/* None option */}
              <button
                type="button"
                onClick={() => setAnnouncementSettings(prev => ({ ...prev, icon: "none" }))}
                style={{
                  padding: "0.5rem 1rem",
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                  border: announcementSettings.icon === "none" ? "3px solid #007bff" : "2px solid #ddd",
                  borderRadius: "8px",
                  background: announcementSettings.icon === "none" ? "#e7f1ff" : "white",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  color: "#666"
                }}
              >
                None
              </button>
              {["⚠️", "📢", "🔔", "ℹ️", "⭐", "🎉", "🏆", "📌", "🚨", "💡"].map(icon => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => setAnnouncementSettings(prev => ({ ...prev, icon }))}
                  style={{
                    padding: "0.5rem 1rem",
                    fontSize: "1.5rem",
                    border: announcementSettings.icon === icon ? "3px solid #007bff" : "2px solid #ddd",
                    borderRadius: "8px",
                    background: announcementSettings.icon === icon ? "#e7f1ff" : "white",
                    cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          {/* Text Size Selection */}
          <div style={{ marginBottom: "1.5rem" }}>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold", color: "#333" }}>
              Text Size
            </label>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              {[
                { value: "small", label: "Small", preview: "0.85rem" },
                { value: "medium", label: "Medium", preview: "1rem" },
                { value: "large", label: "Large", preview: "1.15rem" }
              ].map(size => (
                <button
                  key={size.value}
                  type="button"
                  onClick={() => setAnnouncementSettings(prev => ({ ...prev, textSize: size.value }))}
                  style={{
                    padding: "0.5rem 1.25rem",
                    fontSize: size.preview,
                    border: (announcementSettings.textSize || "medium") === size.value ? "3px solid #007bff" : "2px solid #ddd",
                    borderRadius: "8px",
                    background: (announcementSettings.textSize || "medium") === size.value ? "#e7f1ff" : "white",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    fontWeight: "500"
                  }}
                >
                  {size.label}
                </button>
              ))}
            </div>
          </div>

          {/* Announcement Text */}
          <div style={{ marginBottom: "1.5rem" }}>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold", color: "#333" }}>
              Announcement Text
            </label>
            <TipTapEditor
              value={announcementSettings.text}
              onChange={(html) => setAnnouncementSettings(prev => ({ ...prev, text: html }))}
              placeholder="Enter your announcement message here..."
            />
          </div>

          {/* Preview */}
          {announcementSettings.text && announcementSettings.text !== '<p></p>' && (
            <div style={{ marginBottom: "1.5rem" }}>
              <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold", color: "#333" }}>
                Preview
              </label>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: announcementSettings.icon === "none" ? "0" : "1rem",
                background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                border: "2px solid #f59e0b",
                borderRadius: "12px",
                padding: "1rem 1.5rem",
                boxShadow: "0 4px 15px rgba(245, 158, 11, 0.2)"
              }}>
                {announcementSettings.icon && announcementSettings.icon !== "none" && (
                  <div style={{ fontSize: "2rem", flexShrink: 0 }}>{announcementSettings.icon}</div>
                )}
                <div style={{
                  fontSize: announcementSettings.textSize === "small" ? "0.85rem" : announcementSettings.textSize === "large" ? "1.15rem" : "1rem",
                  color: "#92400e",
                  lineHeight: 1.5
                }}
                  dangerouslySetInnerHTML={{ __html: announcementSettings.text }}
                />
              </div>
            </div>
          )}

          {/* Save Button */}
          <button
            onClick={saveAnnouncementSettings}
            disabled={announcementLoading}
            style={{
              padding: "0.75rem 2rem",
              fontSize: "1rem",
              fontWeight: "bold",
              color: "white",
              background: announcementLoading ? "#6c757d" : "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
              border: "none",
              borderRadius: "8px",
              cursor: announcementLoading ? "not-allowed" : "pointer",
              boxShadow: "0 4px 15px rgba(40, 167, 69, 0.3)"
            }}
          >
            {announcementLoading ? "⏳ Saving..." : "💾 Save Announcement Settings"}
          </button>
        </div>
      )}

      {/* Competitions Tab */}
      {activeTab === "competitions" && (
        <>
          <div style={{ marginBottom: "1rem", padding: "1rem", background: "#f8f9fa", borderRadius: "8px", fontSize: "0.9rem", color: "#666" }}>
            💡 <strong>Tip:</strong> You can drag and drop competitions to reorder them, or use the ⬆️ ⬇️ buttons for precise positioning.
          </div>
          <div style={{ marginBottom: "2rem" }}>
            <button 
              onClick={() => setShowForm(!showForm)}
              className="fancy-add-button"
              disabled={loading}
            >
              <span className="button-icon">✨</span>
              {showForm ? "Cancel" : "Add New Competition"}
              <span className="button-glow"></span>
            </button>
          </div>

      {/* Competition Form */}
      {showForm && (
        <div className="form-container" style={{ marginBottom: "2rem" }}>
          <h2>{editingCompetition ? "Edit Competition" : "Add New Competition"}</h2>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Competition Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Greek Open Championship 2025"
              />
            </div>

            <div className="form-group compact-field">
              <label>Date *</label>
              <input
                type="text"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
                placeholder="e.g., September 15-16, 2025 or 15/09/2025"
              />
            </div>

            <div className="form-group compact-field">
              <label>Location (Optional)</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="e.g., Athens, Greece or Palace Sports Complex"
              />
            </div>

            <div className="form-group compact-field">
              <label>Website (Optional)</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="e.g., https://greekopenchampionship.com"
              />
            </div>

            <div className="form-group compact-field">
              <label>Registration Link (Optional)</label>
              <input
                type="url"
                name="registration"
                value={formData.registration}
                onChange={handleInputChange}
                placeholder="e.g., https://registration.com/greek-open"
              />
            </div>

            <div className="form-group compact-field">
              <label>Schedule Link (Optional)</label>
              <input
                type="url"
                name="schedule"
                value={formData.schedule}
                onChange={handleInputChange}
                placeholder="e.g., https://competition.com/schedule.pdf"
              />
              {formData.schedule && (
                <label style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "0.5rem", 
                  marginTop: "0.5rem",
                  cursor: "pointer",
                  padding: "0.5rem",
                  backgroundColor: formData.scheduleUpdated ? "#ffe6e6" : "#f8f9fa",
                  borderRadius: "6px",
                  border: formData.scheduleUpdated ? "2px solid #dc3545" : "1px solid #ddd"
                }}>
                  <input
                    type="checkbox"
                    checked={formData.scheduleUpdated}
                    onChange={(e) => setFormData(prev => ({ ...prev, scheduleUpdated: e.target.checked }))}
                    style={{ width: "18px", height: "18px", cursor: "pointer" }}
                  />
                  <span style={{ 
                    fontWeight: formData.scheduleUpdated ? "bold" : "normal",
                    color: formData.scheduleUpdated ? "#dc3545" : "#666"
                  }}>
                    {formData.scheduleUpdated ? "🔴 Schedule marked as UPDATED (blinking)" : "Mark as Updated (will blink to attract attention)"}
                  </span>
                </label>
              )}
            </div>

            <div className="form-group compact-field">
              <label>Results Link (Optional)</label>
              <input
                type="url"
                name="results"
                value={formData.results}
                onChange={handleInputChange}
                placeholder="e.g., https://results.com/greek-open-2025"
              />
              <p style={{ fontSize: "0.8rem", color: "#666", marginTop: "0.5rem" }}>
                <strong>Note:</strong> Adding a results link will automatically move this competition to "Past Competitions"
              </p>
            </div>

            <div className="form-group">
              <label>Competition Logo</label>
              <input
                type="file"
                name="logo"
                onChange={handleInputChange}
                accept="image/*"
              />
              {formData.logoUrl && (
                <div style={{ marginTop: "0.5rem" }}>
                  <img 
                    src={formData.logoUrl} 
                    alt="Current logo" 
                    style={{ width: "60px", height: "60px", objectFit: "contain" }}
                  />
                  <p style={{ fontSize: "0.8rem", color: "#666" }}>Current logo</p>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Categories</label>
              <div className="categories-selector-fancy">
                {availableCategories.map(category => {
                  const isSelected = formData.categories.includes(category);
                  return (
                    <label key={category} className={`category-fancy-item ${isSelected ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        name="categories"
                        value={category}
                        checked={isSelected}
                        onChange={handleInputChange}
                        className="category-checkbox"
                      />
                      <div className="category-content">
                        <span className="category-icon">
                          {category.includes('Latin') ? '💃' : category.includes('Contemporary') ? '🎭' : '🎤'}
                        </span>
                        <span className="category-label-fancy">
                          {category}
                        </span>
                        <span className="category-checkmark">✓</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="form-group compact-field">
              <label>Display Order</label>
              <input
                type="number"
                name="sortOrder"
                value={formData.sortOrder}
                onChange={handleInputChange}
                placeholder="0"
                min="0"
                step="1"
              />
              <p style={{ fontSize: "0.8rem", color: "#666", marginTop: "0.5rem" }}>
                Lower numbers appear first. Use 0, 10, 20, etc. to leave room for reordering.
              </p>
            </div>

            {/* Playlist Section */}
            <div className="form-group">
              <label>🎵 Competition Playlist</label>
              <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "0.75rem" }}>
                Upload audio tracks for dancers to practice with. Drag to reorder tracks.
              </p>
              
              {/* Existing tracks */}
              {formData.playlist && formData.playlist.length > 0 && (
                <div className="existing-tracks-list" style={{ marginBottom: "1.5rem" }}>
                  <p style={{ fontSize: "0.9rem", fontWeight: "600", marginBottom: "0.75rem", color: "#333" }}>
                    📂 Current Tracks ({formData.playlist.length}) - Drag to reorder:
                  </p>
                  {formData.playlist.map((track, index) => (
                    <div 
                      key={index} 
                      className="playlist-track-edit-item" 
                      draggable
                      onDragStart={() => handleTrackDragStart(index)}
                      onDragOver={(e) => handleTrackDragOver(e, index)}
                      onDragEnd={handleTrackDragEnd}
                      style={{
                        padding: "0.75rem",
                        background: draggedTrackIndex === index ? "#e3f2fd" : "#f0f8ff",
                        borderRadius: "8px",
                        marginBottom: "0.75rem",
                        border: draggedTrackIndex === index ? "2px dashed #2196f3" : "1px solid #d0e8ff",
                        cursor: "grab",
                        opacity: draggedTrackIndex === index ? 0.7 : 1
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <span style={{ cursor: "grab", fontSize: "1.1rem" }} title="Drag to reorder">⋮⋮</span>
                          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                            <button type="button" onClick={() => moveTrackUp(index)} disabled={index === 0}
                              style={{ background: "none", border: "none", cursor: index === 0 ? "not-allowed" : "pointer", padding: "0", fontSize: "0.7rem", opacity: index === 0 ? 0.3 : 1 }}>
                              ▲
                            </button>
                            <button type="button" onClick={() => moveTrackDown(index)} disabled={index === formData.playlist.length - 1}
                              style={{ background: "none", border: "none", cursor: index === formData.playlist.length - 1 ? "not-allowed" : "pointer", padding: "0", fontSize: "0.7rem", opacity: index === formData.playlist.length - 1 ? 0.3 : 1 }}>
                              ▼
                            </button>
                          </div>
                          <span style={{ fontWeight: "500", color: "#666" }}>#{index + 1}</span>
                          <span style={{ fontWeight: "500" }}>🎵 {track.fileName || track.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeExistingTrack(index)}
                          style={{
                            background: "#dc3545",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            padding: "0.25rem 0.5rem",
                            cursor: "pointer",
                            fontSize: "0.75rem"
                          }}
                        >
                          ✕ Remove
                        </button>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "0.5rem", fontSize: "0.85rem" }}>
                        <div>
                          <label style={{ fontSize: "0.75rem", color: "#666" }}>Dance</label>
                          <select
                            value={track.dance || ""}
                            onChange={(e) => updateExistingTrackMetadata(index, "dance", e.target.value)}
                            style={{ width: "100%", padding: "0.35rem", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.85rem" }}
                          >
                            <option value="">-- Select Dance --</option>
                            {danceNames.map(dance => (
                              <option key={dance} value={dance}>{dance}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: "0.75rem", color: "#666" }}>Display Name</label>
                          <input
                            type="text"
                            value={track.displayName || track.name || ""}
                            onChange={(e) => updateExistingTrackMetadata(index, "displayName", e.target.value)}
                            style={{ width: "100%", padding: "0.35rem", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.85rem" }}
                            placeholder="Track name..."
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: "0.75rem", color: "#666" }}>Category</label>
                          <select
                            value={track.category || ""}
                            onChange={(e) => updateExistingTrackMetadata(index, "category", e.target.value)}
                            style={{ width: "100%", padding: "0.35rem", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.85rem" }}
                          >
                            <option value="">-- Select Category --</option>
                            {playlistCategories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: "0.75rem", color: "#666" }}>Round</label>
                          <select
                            value={track.round || ""}
                            onChange={(e) => updateExistingTrackMetadata(index, "round", e.target.value)}
                            style={{ width: "100%", padding: "0.35rem", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.85rem" }}
                          >
                            <option value="">-- Select Round --</option>
                            {playlistRounds.map(round => (
                              <option key={round} value={round}>{round}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Default category/round for new uploads */}
              <div style={{ marginBottom: "1rem", padding: "0.75rem", background: "#f8f9fa", borderRadius: "8px", border: "1px solid #e0e0e0" }}>
                <p style={{ fontSize: "0.85rem", fontWeight: "600", marginBottom: "0.5rem", color: "#555" }}>
                  📁 Default Settings for New Uploads:
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.75rem" }}>
                  <div>
                    <label style={{ fontSize: "0.75rem", color: "#666" }}>Dance</label>
                    <select
                      value={newTrackDance}
                      onChange={(e) => setNewTrackDance(e.target.value)}
                      style={{ width: "100%", padding: "0.4rem", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.85rem" }}
                    >
                      <option value="">-- No Dance --</option>
                      {danceNames.map(dance => (
                        <option key={dance} value={dance}>{dance}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "0.75rem", color: "#666" }}>Category</label>
                    <select
                      value={newTrackCategory}
                      onChange={(e) => setNewTrackCategory(e.target.value)}
                      style={{ width: "100%", padding: "0.4rem", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.85rem" }}
                    >
                      <option value="">-- No Category --</option>
                      {playlistCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "0.75rem", color: "#666" }}>Round</label>
                    <select
                      value={newTrackRound}
                      onChange={(e) => setNewTrackRound(e.target.value)}
                      style={{ width: "100%", padding: "0.4rem", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.85rem" }}
                    >
                      <option value="">-- No Round --</option>
                      {playlistRounds.map(round => (
                        <option key={round} value={round}>{round}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* New files to upload */}
              {playlistFiles.length > 0 && (
                <div className="new-tracks-list" style={{ marginBottom: "1rem" }}>
                  <p style={{ fontSize: "0.9rem", fontWeight: "600", marginBottom: "0.75rem", color: "#28a745" }}>
                    ➕ New Tracks to Upload ({playlistFiles.length}):
                  </p>
                  {playlistFiles.map((fileItem, index) => (
                    <div key={index} className="playlist-track-edit-item" style={{
                      padding: "0.75rem",
                      background: "#e8f5e9",
                      borderRadius: "8px",
                      marginBottom: "0.75rem",
                      border: "1px solid #c8e6c9"
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: "500", color: "#2e7d32" }}>
                          🎵 {fileItem.file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => removePlaylistFile(index)}
                          style={{
                            background: "#dc3545",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            padding: "0.25rem 0.5rem",
                            cursor: "pointer",
                            fontSize: "0.75rem"
                          }}
                        >
                          ✕ Remove
                        </button>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "0.5rem", fontSize: "0.85rem" }}>
                        <div>
                          <label style={{ fontSize: "0.75rem", color: "#666" }}>Dance</label>
                          <select
                            value={fileItem.dance}
                            onChange={(e) => updatePlaylistFileMetadata(index, "dance", e.target.value)}
                            style={{ width: "100%", padding: "0.35rem", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.85rem" }}
                          >
                            <option value="">-- Select Dance --</option>
                            {danceNames.map(dance => (
                              <option key={dance} value={dance}>{dance}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: "0.75rem", color: "#666" }}>Display Name</label>
                          <input
                            type="text"
                            value={fileItem.displayName}
                            onChange={(e) => updatePlaylistFileMetadata(index, "displayName", e.target.value)}
                            style={{ width: "100%", padding: "0.35rem", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.85rem" }}
                            placeholder="Track name..."
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: "0.75rem", color: "#666" }}>Category</label>
                          <select
                            value={fileItem.category}
                            onChange={(e) => updatePlaylistFileMetadata(index, "category", e.target.value)}
                            style={{ width: "100%", padding: "0.35rem", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.85rem" }}
                          >
                            <option value="">-- Select Category --</option>
                            {playlistCategories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: "0.75rem", color: "#666" }}>Round</label>
                          <select
                            value={fileItem.round}
                            onChange={(e) => updatePlaylistFileMetadata(index, "round", e.target.value)}
                            style={{ width: "100%", padding: "0.35rem", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.85rem" }}
                          >
                            <option value="">-- Select Round --</option>
                            {playlistRounds.map(round => (
                              <option key={round} value={round}>{round}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <input
                type="file"
                accept="audio/*"
                multiple
                onChange={handlePlaylistFileChange}
                style={{ marginTop: "0.5rem" }}
              />
              {uploadingPlaylist && (
                <p style={{ color: "#007bff", fontSize: "0.85rem", marginTop: "0.5rem" }}>
                  ⏳ Uploading playlist tracks...
                </p>
              )}
            </div>

            {/* Prize Money Section */}
            <div className="form-group">
              <label>💰 Prize Money (Optional)</label>
              <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "0.75rem" }}>
                Add prize money information with categories (e.g., Latin Solo, Hip Hop Group). All amounts in EUR (€).
              </p>

              {/* Prize Money Title */}
              <div style={{ marginBottom: "1rem" }}>
                <label style={{ fontSize: "0.85rem", fontWeight: "600", color: "#333" }}>General Title (Optional)</label>
                <input
                  type="text"
                  value={formData.prizeMoney?.title || ""}
                  onChange={(e) => {
                    initializePrizeMoney();
                    updatePrizeMoneyTitle(e.target.value);
                  }}
                  placeholder="e.g., Prize Money Distribution"
                  style={{ 
                    width: "100%", 
                    padding: "0.5rem", 
                    borderRadius: "6px", 
                    border: "1px solid #ddd",
                    marginTop: "0.25rem"
                  }}
                />
              </div>

              {/* Add New Category */}
              <div style={{ 
                display: "flex", 
                gap: "0.5rem", 
                marginBottom: "1rem",
                padding: "0.75rem",
                background: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e0e0e0"
              }}>
                <input
                  type="text"
                  value={newPrizeCategory}
                  onChange={(e) => setNewPrizeCategory(e.target.value)}
                  placeholder="New category name (e.g., Latin Solo Adult)"
                  style={{ 
                    flex: 1, 
                    padding: "0.5rem", 
                    borderRadius: "6px", 
                    border: "1px solid #ddd" 
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPrizeCategory())}
                />
                <button
                  type="button"
                  onClick={addPrizeCategory}
                  style={{
                    background: "#28a745",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    padding: "0.5rem 1rem",
                    cursor: "pointer",
                    fontWeight: "600"
                  }}
                >
                  + Add Category
                </button>
              </div>

              {/* Category Tabs */}
              {formData.prizeMoney?.categories && Object.keys(formData.prizeMoney.categories).length > 0 && (
                <div style={{ marginBottom: "1rem" }}>
                  <div style={{ 
                    display: "flex", 
                    flexWrap: "wrap", 
                    gap: "0.5rem", 
                    marginBottom: "1rem",
                    borderBottom: "2px solid #e0e0e0",
                    paddingBottom: "0.75rem"
                  }}>
                    {Object.keys(formData.prizeMoney.categories).map(catName => (
                      <button
                        key={catName}
                        type="button"
                        onClick={() => setActivePrizeCategory(catName)}
                        style={{
                          padding: "0.5rem 1rem",
                          borderRadius: "20px",
                          border: activePrizeCategory === catName ? "2px solid #007bff" : "1px solid #ddd",
                          background: activePrizeCategory === catName ? "#e7f1ff" : "white",
                          color: activePrizeCategory === catName ? "#007bff" : "#333",
                          cursor: "pointer",
                          fontWeight: activePrizeCategory === catName ? "600" : "normal",
                          transition: "all 0.2s ease"
                        }}
                      >
                        {catName}
                      </button>
                    ))}
                  </div>

                  {/* Active Category Prizes */}
                  {activePrizeCategory && formData.prizeMoney.categories[activePrizeCategory] && (
                    <div style={{ 
                      padding: "1rem", 
                      background: "#f8f9fa", 
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0"
                    }}>
                      <div style={{ 
                        display: "flex", 
                        justifyContent: "space-between", 
                        alignItems: "center",
                        marginBottom: "1rem"
                      }}>
                        <h4 style={{ margin: 0, color: "#333" }}>🏆 {activePrizeCategory}</h4>
                        <button
                          type="button"
                          onClick={() => removePrizeCategory(activePrizeCategory)}
                          style={{
                            background: "#dc3545",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            padding: "0.25rem 0.75rem",
                            cursor: "pointer",
                            fontSize: "0.85rem"
                          }}
                        >
                          🗑️ Remove Category
                        </button>
                      </div>

                      {/* Prize List */}
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                        {formData.prizeMoney.categories[activePrizeCategory]?.prizes?.map((prize, index) => (
                          <div 
                            key={index} 
                            style={{ 
                              display: "grid", 
                              gridTemplateColumns: "1fr 1fr auto", 
                              gap: "0.5rem",
                              alignItems: "center"
                            }}
                          >
                            <input
                              type="text"
                              value={prize.place}
                              onChange={(e) => updatePrize(activePrizeCategory, index, "place", e.target.value)}
                              placeholder="Place (e.g., 1st, 2nd)"
                              style={{ 
                                padding: "0.5rem", 
                                borderRadius: "4px", 
                                border: "1px solid #ddd" 
                              }}
                            />
                            <div style={{ position: "relative" }}>
                              <input
                                type="number"
                                value={prize.amount}
                                onChange={(e) => updatePrize(activePrizeCategory, index, "amount", e.target.value)}
                                placeholder="Amount"
                                min="0"
                                step="1"
                                style={{ 
                                  width: "100%",
                                  padding: "0.5rem", 
                                  paddingRight: "2rem",
                                  borderRadius: "4px", 
                                  border: "1px solid #ddd" 
                                }}
                              />
                              <span style={{ 
                                position: "absolute", 
                                right: "0.75rem", 
                                top: "50%", 
                                transform: "translateY(-50%)",
                                color: "#666",
                                fontWeight: "bold"
                              }}>€</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removePrize(activePrizeCategory, index)}
                              style={{
                                background: "#dc3545",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                width: "32px",
                                height: "32px",
                                cursor: "pointer",
                                fontSize: "1rem"
                              }}
                              title="Remove prize"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={() => addPrizeToCategory(activePrizeCategory)}
                        style={{
                          marginTop: "0.75rem",
                          background: "#007bff",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          padding: "0.4rem 0.75rem",
                          cursor: "pointer",
                          fontSize: "0.85rem"
                        }}
                      >
                        + Add Prize
                      </button>
                    </div>
                  )}

                  {/* Clear All Button */}
                  <button
                    type="button"
                    onClick={clearAllPrizeMoney}
                    style={{
                      marginTop: "1rem",
                      background: "transparent",
                      color: "#dc3545",
                      border: "1px solid #dc3545",
                      borderRadius: "4px",
                      padding: "0.4rem 0.75rem",
                      cursor: "pointer",
                      fontSize: "0.85rem"
                    }}
                  >
                    🗑️ Clear All Prize Money
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
              <button 
                type="submit" 
                className="button-primary"
                disabled={loading}
              >
                {loading ? "Saving..." : (editingCompetition ? "Update Competition" : "Add Competition")}
              </button>
              
              <button 
                type="button" 
                onClick={cancelEdit}
                className="button-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Competitions List */}
      <div>
        <h2>Current Competitions</h2>
        {loading && !showForm ? (
          <p>Loading competitions...</p>
        ) : competitions.length > 0 ? (
          <div>
            {/* Upcoming Competitions */}
            <div style={{ marginBottom: "3rem" }}>
              <h3 style={{ color: "#1a365d", borderBottom: "2px solid #ffd700", paddingBottom: "0.5rem" }}>
                📅 Upcoming Competitions
              </h3>
              {competitions.filter(comp => comp.type === "upcoming").length > 0 ? (
                <div className="competitions-admin-list">
                  {competitions.filter(comp => comp.type === "upcoming").map(competition => (
                    <div 
                      key={competition.id} 
                      className={`competition-admin-card-enhanced ${dragOverItem?.id === competition.id ? 'drag-over' : ''} ${competition.cancelled ? 'competition-cancelled' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, competition)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDragEnter={(e) => handleDragEnter(e, competition)}
                      onDrop={(e) => handleDrop(e, competition)}
                      style={{ cursor: 'move' }}
                    >
                      {competition.cancelled && (
                        <div className="cancelled-badge" title={`Cancelled: ${competition.cancellationReason || 'No reason provided'}`}>
                          ❌ CANCELLED
                        </div>
                      )}
                      <div className="drag-handle" title="Drag to reorder">
                        ⋮⋮
                      </div>
                      <div className="competition-admin-header">
                        {competition.logoUrl && (
                          <img 
                            src={competition.logoUrl} 
                            alt={`${competition.name} logo`}
                            className="competition-admin-logo"
                          />
                        )}
                        <div className="competition-admin-info">
                          <h3>{competition.name}</h3>
                          <p><strong>Date:</strong> {formatDate(competition.date)}</p>
                          {competition.website && (
                            <p><strong>Website:</strong> <a href={competition.website} target="_blank" rel="noopener noreferrer">View</a></p>
                          )}
                          {competition.registration && (
                            <p><strong>Registration:</strong> <a href={competition.registration} target="_blank" rel="noopener noreferrer">View</a></p>
                          )}
                          {competition.schedule && (
                            <p><strong>Schedule:</strong> <a href={competition.schedule} target="_blank" rel="noopener noreferrer">View</a></p>
                          )}
                          {competition.results && (
                            <p><strong>Results:</strong> <a href={competition.results} target="_blank" rel="noopener noreferrer">View</a></p>
                          )}
                          {competition.categories && competition.categories.length > 0 && (
                            <p><strong>Categories:</strong> {competition.categories.length} selected</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="competition-quick-results">
                        <input
                          type="url"
                          placeholder="Add results URL..."
                          value={quickResultsEdit[competition.id] || ""}
                          onChange={(e) => handleQuickResultsChange(competition.id, e.target.value)}
                          className="quick-results-input"
                        />
                        <button
                          onClick={() => saveQuickResults(competition)}
                          className="quick-save-btn"
                          disabled={loading || !quickResultsEdit[competition.id]}
                          title="Save results and move to Past"
                        >
                          💾
                        </button>
                      </div>
                      
                      <div className="competition-admin-actions">
                        <div className="sort-buttons">
                          <button 
                            onClick={() => moveCompetitionUp(competition)}
                            className="btn-sort"
                            disabled={loading}
                            title="Move up"
                          >
                            ⬆️
                          </button>
                          <span className="sort-order-display">#{competition.sortOrder || 0}</span>
                          <button 
                            onClick={() => moveCompetitionDown(competition)}
                            className="btn-sort"
                            disabled={loading}
                            title="Move down"
                          >
                            ⬇️
                          </button>
                        </div>
                        <button 
                          onClick={() => handleEdit(competition)}
                          className="btn-edit"
                          disabled={loading}
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          onClick={() => moveToPast(competition)}
                          className="btn-move"
                          disabled={loading}
                        >
                          📋 Move to Past
                        </button>
                        <button 
                          onClick={() => toggleCancellation(competition)}
                          className={`btn-cancel ${competition.cancelled ? 'btn-cancelled' : ''}`}
                          disabled={loading}
                        >
                          {competition.cancelled ? '✅ Restore' : '❌ Cancel'}
                        </button>
                        <button 
                          onClick={() => handleDelete(competition)}
                          className="btn-delete"
                          disabled={loading}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: "#666", fontStyle: "italic" }}>No upcoming competitions</p>
              )}
            </div>

            {/* Past Competitions */}
            <div>
              <h3 style={{ color: "#666", borderBottom: "2px solid #ccc", paddingBottom: "0.5rem" }}>
                🏆 Past Competitions
              </h3>
              {competitions.filter(comp => comp.type === "past").length > 0 ? (
                <div className="competitions-admin-list">
                  {competitions.filter(comp => comp.type === "past").map(competition => (
                    <div 
                      key={competition.id} 
                      className={`competition-admin-card ${dragOverItem?.id === competition.id ? 'drag-over' : ''} ${competition.cancelled ? 'competition-cancelled' : ''}`}
                      style={{ opacity: 0.8, cursor: 'move' }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, competition)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDragEnter={(e) => handleDragEnter(e, competition)}
                      onDrop={(e) => handleDrop(e, competition)}
                    >
                      {competition.cancelled && (
                        <div className="cancelled-badge" title={`Cancelled: ${competition.cancellationReason || 'No reason provided'}`}>
                          ❌ CANCELLED
                        </div>
                      )}
                      <div className="drag-handle" title="Drag to reorder">
                        ⋮⋮
                      </div>
                      <div className="competition-admin-header">
                        {competition.logoUrl && (
                          <img 
                            src={competition.logoUrl} 
                            alt={`${competition.name} logo`}
                            className="competition-admin-logo"
                          />
                        )}
                        <div className="competition-admin-info">
                          <h3>{competition.name}</h3>
                          <p><strong>Date:</strong> {formatDate(competition.date)}</p>
                          {competition.website && (
                            <p><strong>Website:</strong> <a href={competition.website} target="_blank" rel="noopener noreferrer">View</a></p>
                          )}
                          {competition.registration && (
                            <p><strong>Registration:</strong> <a href={competition.registration} target="_blank" rel="noopener noreferrer">View</a></p>
                          )}
                          {competition.schedule && (
                            <p><strong>Schedule:</strong> <a href={competition.schedule} target="_blank" rel="noopener noreferrer">View</a></p>
                          )}
                          {competition.results && (
                            <p><strong>Results:</strong> <a href={competition.results} target="_blank" rel="noopener noreferrer">View</a></p>
                          )}
                          {competition.categories && competition.categories.length > 0 && (
                            <p><strong>Categories:</strong> {competition.categories.length} selected</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="competition-admin-actions">
                        <div className="sort-buttons">
                          <button 
                            onClick={() => moveCompetitionUp(competition)}
                            className="btn-sort"
                            disabled={loading}
                            title="Move up"
                          >
                            ⬆️
                          </button>
                          <span className="sort-order-display">#{competition.sortOrder || 0}</span>
                          <button 
                            onClick={() => moveCompetitionDown(competition)}
                            className="btn-sort"
                            disabled={loading}
                            title="Move down"
                          >
                            ⬇️
                          </button>
                        </div>
                        <button 
                          onClick={() => handleEdit(competition)}
                          className="btn-edit"
                          disabled={loading}
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          onClick={() => moveToUpcoming(competition)}
                          className="btn-move"
                          disabled={loading}
                          title="Move back to Upcoming Competitions"
                        >
                          ⏪ Move to Upcoming
                        </button>
                        <button 
                          onClick={() => toggleCancellation(competition)}
                          className={`btn-cancel ${competition.cancelled ? 'btn-cancelled' : ''}`}
                          disabled={loading}
                        >
                          {competition.cancelled ? '✅ Restore' : '❌ Cancel'}
                        </button>
                        <button 
                          onClick={() => handleDelete(competition)}
                          className="btn-delete"
                          disabled={loading}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: "#666", fontStyle: "italic" }}>No past competitions</p>
              )}
            </div>
          </div>
        ) : (
          <p>No competitions found. Add your first competition above!</p>
        )}
      </div>
      </>
      )}

      {/* Rules & Regulations Tab */}
      {activeTab === "rules" && (
        <div className="rules-editor">
          <h2>📋 Rules & Regulations</h2>
          <p>Edit the rules and regulations content that will be displayed on the homepage.</p>
          
          {/* Sub-tabs for Solo/Group */}
          <div className="rules-sub-tabs">
            <button 
              className={`sub-tab-button ${rulesSubTab === "solo" ? "active" : ""}`}
              onClick={() => setRulesSubTab("solo")}
            >
              🕺 Solo
            </button>
            <button 
              className={`sub-tab-button ${rulesSubTab === "group" ? "active" : ""}`}
              onClick={() => setRulesSubTab("group")}
            >
              👥 Group
            </button>
          </div>

          {/* Solo Rules Editor */}
          {rulesSubTab === "solo" && (
            <div className="rules-content">
              <div className="form-group">
                <label>Solo Rules & Regulations Content</label>
                <TipTapEditor
                  value={rulesContentSolo}
                  onChange={(html) => setRulesContentSolo(html)}
                  placeholder="Enter the Solo rules and regulations content here..."
                />
              </div>

              <div style={{ marginTop: "1.5rem" }}>
                <button
                  onClick={saveRulesContentSolo}
                  className="fancy-save-button"
                  disabled={loading}
                >
                  <span className="button-icon">💾</span>
                  {loading ? "Saving..." : "Save Solo Rules"}
                </button>
              </div>
            </div>
          )}

          {/* Group Rules Editor */}
          {rulesSubTab === "group" && (
            <div className="rules-content">
              <div className="form-group">
                <label>Group Rules & Regulations Content</label>
                <TipTapEditor
                  value={rulesContentGroup}
                  onChange={(html) => setRulesContentGroup(html)}
                  placeholder="Enter the Group rules and regulations content here..."
                />
              </div>

              <div style={{ marginTop: "1.5rem" }}>
                <button
                  onClick={saveRulesContentGroup}
                  className="fancy-save-button"
                  disabled={loading}
                >
                  <span className="button-icon">💾</span>
                  {loading ? "Saving..." : "Save Group Rules"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
}
